import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Phone, PhoneOff, Copy, X, Clapperboard } from "lucide-react";
import api from "@/lib/api";

// Per-video invalshoek voor de gespreksopener — sluit aan op wat de lead
// effectief bekeken heeft, zodat het gesprek meteen relevant voelt.
const ANGLE_BY_SLUG = {
    "video-1": "met de makelaar zelf in beeld — dat werkt ook heel sterk voor je persoonlijke branding",
    "video-2": "opgenomen op locatie en versterkt met AI-shots — snel geschoten, groots resultaat",
    "video-3": "volledig gemaakt op basis van bestaande foto's — geen opnames nodig, dus heel budgetvriendelijk om te starten",
};

/**
 * CallNowDialog — voor warme leads met een 🎬-badge. Toont het telefoonnummer
 * (klikbaar via tel:) + een korte gespreksopener op maat van de bekeken video.
 */
export const CallNowDialog = ({ lead, onClose }) => {
    const [titles, setTitles] = useState({});

    useEffect(() => {
        api.get("/admin/outreach/videos")
            .then((r) => {
                const map = {};
                (r.data.videos || []).forEach((v) => { map[v.slug] = v.title; });
                setTitles(map);
            })
            .catch(() => {});
    }, []);

    const watched = lead.videos_clicked || [];
    const firstName = (lead.name || "").trim().split(" ")[0] || "";

    const opener = useMemo(() => {
        const slug = watched[0];
        const title = titles[slug] || slug || "onze video";
        const kantoor = lead.company ? ` zoals ${lead.company}` : "";
        if (slug === "demo-retouch") {
            // Lead watched the AI-tool demo → pitch the tool + free credits.
            return (
                `Dag ${firstName || "daar"}, met Cyril van Social Factory.\n\n` +
                `Ik zag dat je de demo van onze AI-fototool bekeken hebt — leuk! ` +
                `Daarmee bewerk je vastgoedfoto's in seconden: grijze lucht wordt blauw, een lege ruimte smaakvol ingericht.\n\n` +
                `Je hebt nog 10 gratis credits klaarstaan om het zelf te testen. ` +
                `Heb je 2 minuutjes? Dan help ik je op weg en vertel ik ook kort wat onze woningvideo's voor makelaars${kantoor} doen.`
            );
        }
        const angle = ANGLE_BY_SLUG[slug] || "";
        const extra = watched.length > 1 ? ` (en nog ${watched.length - 1} andere)` : "";
        return (
            `Dag ${firstName || "daar"}, met Cyril van Social Factory.\n\n` +
            `Ik zag dat je onze video "${title}" bekeken hebt${extra} — leuk! ` +
            `Ik maak precies dat soort video's${angle ? `, ${angle}` : ""}, voor makelaars${kantoor}.\n\n` +
            `Heb je 2 minuutjes? Dan vertel ik kort wat zo'n video voor jouw panden kan opleveren en wat het kost.`
        );
    }, [watched, titles, firstName, lead.company]);

    const copyOpener = async () => {
        try {
            await navigator.clipboard.writeText(opener);
            toast.success("Gespreksopener gekopieerd");
        } catch {
            // Fallback voor browsers zonder clipboard-API-permissie
            try {
                const ta = document.createElement("textarea");
                ta.value = opener;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                toast.success("Gespreksopener gekopieerd");
            } catch {
                toast.error("Kopiëren mislukt — selecteer de tekst handmatig");
            }
        }
    };

    const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="call-now-dialog">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-lg bg-white border border-[#E5A93D]/30 rounded-md shadow-2xl p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-900" data-testid="call-now-close">
                    <X className="h-5 w-5" />
                </button>

                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#E5A93D] font-bold mb-1">
                    <Clapperboard className="h-3.5 w-3.5" /> Warme lead — bekeek je video
                </div>
                <h2 className="font-display text-xl font-bold mb-0.5">{lead.name || lead.email}</h2>
                <p className="text-xs text-zinc-500 mb-4">
                    {lead.company && <span>{lead.company} · </span>}
                    {watched.map((s) => titles[s] || s).join(" · ")}
                    {lead.last_video_click_at && <span> · bekeken {fmtDT(lead.last_video_click_at)}</span>}
                </p>

                {/* Telefoon */}
                {lead.phone ? (
                    <a
                        href={`tel:${lead.phone.replace(/[\s./-]/g, "")}`}
                        className="flex items-center justify-center gap-2.5 h-12 mb-4 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm text-lg transition-colors active:scale-[0.99]"
                        data-testid="call-now-phone"
                    >
                        <Phone className="h-5 w-5" /> {lead.phone}
                    </a>
                ) : (
                    <div className="flex items-center gap-2.5 h-12 mb-4 px-4 border border-zinc-200 rounded-sm text-zinc-500 text-sm" data-testid="call-now-no-phone">
                        <PhoneOff className="h-4 w-4" /> Geen telefoonnummer bekend
                        {lead.website && (
                            <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="ml-auto text-[#E5A93D] hover:underline text-xs">
                                Zoek op website →
                            </a>
                        )}
                    </div>
                )}

                {/* Gespreksopener */}
                <div className="border border-zinc-200 rounded-sm bg-white">
                    <div className="flex items-center justify-between px-4 pt-3">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Gespreksopener</span>
                        <button
                            onClick={copyOpener}
                            className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-[#E5A93D]"
                            data-testid="call-now-copy-opener"
                        >
                            <Copy className="h-3 w-3" /> Kopieer
                        </button>
                    </div>
                    <p className="px-4 py-3 text-sm text-zinc-800 leading-relaxed whitespace-pre-line" data-testid="call-now-opener-text">
                        {opener}
                    </p>
                </div>

                <p className="mt-3 text-[11px] text-zinc-400">
                    Tip: bel binnen 24u na de videoklik — dan zit je nog top-of-mind.
                </p>
            </div>
        </div>
    );
};
